// Copyright (c) Microsoft. All rights reserved.

import {
  AvatarProps,
  Button,
  Persona,
  Text,
  ToggleButton,
  // ToggleButton,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
} from '@fluentui/react-components';
import {
  ChevronDown20Regular,
  ChevronUp20Regular,
  // ChevronDown20Regular,
  // ChevronUp20Regular,
  Clipboard20Regular,
  ClipboardTask20Regular,
  ThumbDislikeFilled,
  ThumbLikeFilled,
} from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useChat } from '../../../libs/hooks/useChat';
import { AuthorRoles, ChatMessageType, IChatMessage, UserFeedback } from '../../../libs/models/ChatMessage';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { DefaultChatUser, FeatureKeys } from '../../../redux/features/app/AppState';
import { Breakpoints, customTokens } from '../../../styles';
import { timestampToDateString } from '../../utils/TextUtils';
import { PlanViewer } from '../plan-viewer/PlanViewer';
import { PromptDialog } from '../prompt-dialog/PromptDialog';
import { TypingIndicator } from '../typing-indicator/TypingIndicator';
import * as utils from './../../utils/TextUtils';
import { ChatHistoryDocumentContent } from './ChatHistoryDocumentContent';
import { ChatHistoryTextContent } from './ChatHistoryTextContent';
// import { CitationCards } from './CitationCards';
import { UserFeedbackActions } from './UserFeedbackActions';
import AdaptiveCard from './AdaptiveCardItem';
import { Constants } from '../../../Constants';
import { APIProvider, Map } from '@vis.gl/react-google-maps';
import { CitationCards } from './CitationCards';
// import './table.css';

const useClasses = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '75%',
    minWidth: '24em',
    ...shorthands.borderRadius(customTokens.borderRadiusMedium),
    ...Breakpoints.small({
      maxWidth: '100%',
    }),
    ...shorthands.gap(customTokens.spacingHorizontalXS),
  },
  debug: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
  },
  alignEnd: {
    alignSelf: 'flex-end',
  },
  persona: {
    paddingTop: customTokens.spacingVerticalS,
  },
  item: {
    backgroundColor: customTokens.colorNeutralBackground1,
    ...shorthands.borderRadius(customTokens.borderRadiusMedium),
    ...shorthands.padding(customTokens.spacingVerticalXS, customTokens.spacingHorizontalS),
  },
  me: {
    backgroundColor: customTokens.colorMeBackground,
    width: '100%',
  },
  time: {
    color: customTokens.colorNeutralForeground3,
    fontSize: customTokens.fontSizeBase200,
    fontWeight: 400,
  },
  header: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    ...shorthands.gap(customTokens.spacingHorizontalL),
  },
  canvas: {
    width: '100%',
    textAlign: 'center',
  },
  image: {
    maxWidth: '250px',
  },
  blur: {
    filter: 'blur(5px)',
  },
  controls: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: customTokens.spacingVerticalS,
    marginBottom: customTokens.spacingVerticalS,
    ...shorthands.gap(customTokens.spacingHorizontalL),
  },
  citationButton: {
    marginRight: 'auto',
  },
  rlhf: {
    marginLeft: 'auto',
  },
  orangeButton: {
    color: customTokens.connectOrange,
    '&:hover': {
      color: customTokens.connectBlue
    }
  },
  // table: {
  //   width: '100%',
  //   borderCollapse: 'collapse',
  //   backgroundColor: 'var(--color-neutral-background1)', // Custom token
  //   ...shorthands.border('1px', 'solid', 'var(--colorBrandForeground1)'), // Shorthand for border
  // },
  // thead: {
  //   backgroundColor: 'var(--color-neutral-background3)', // Custom token
  // },
  // th: {
  //   padding: '10px',
  //   textAlign: 'left',
  //   ...shorthands.border('1px', 'solid', 'var(--colorBrandForeground1)'),
  //   backgroundColor: 'var(--color-neutral-background3)', // Custom token
  //   fontWeight: 'bold',
  // },
  // td: {
  //   padding: '10px',
  //   textAlign: 'left',
  //   ...shorthands.border('1px', 'solid', 'var(--colorBrandForeground1)'),
  //   backgroundColor: 'var(--color-neutral-background2)', // Custom token
  // },
  // evenRow: {
  //   backgroundColor: '#F5F5F5', // Light gray for even rows
  // },
  // oddRow: {
  //   backgroundColor: '#FFFFFF', // White for odd rows
  // },

  // table: {
  //   width: '100%',
  //   borderCollapse: 'collapse',
  //   backgroundColor: 'var(--color-neutral-background1)', // Custom token
  //   ...shorthands.border('1px', 'solid', 'var(--colorBrandForeground1)'),

  //   // Nested styles for table elements
  //   thead: {
  //     backgroundColor: 'var(--color-neutral-background3)',
  //   },
  //   th: {
  //     padding: '10px',
  //     textAlign: 'left',
  //     ...shorthands.border('1px', 'solid', 'var(--colorBrandForeground1)'),
  //     backgroundColor: 'var(--color-neutral-background3)', // Custom token
  //     fontWeight: 'bold',
  //   },
  //   td: {
  //     padding: '10px',
  //     textAlign: 'left',
  //     ...shorthands.border('1px', 'solid', 'var(--colorBrandForeground1)'),
  //     backgroundColor: 'var(--color-neutral-background2)', // Custom token
  //   },
  //   'tbody tr:nth-child(even)': {
  //     backgroundColor: '#F5F5F5', // Light gray for even rows
  //   },
  //   'tbody tr:nth-child(odd)': {
  //     backgroundColor: '#FFFFFF', // White for odd rows
  //   }
  // }
  customTable: {
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'var(--color-neutral-background1)', // Custom token
    },
    '& th': {
      padding: '10px',
      textAlign: 'left',
      ...shorthands.border('1px', 'solid', 'var(--colorBrandForeground1)'),
      backgroundColor: 'var(--color-neutral-background3)', // Custom token
      fontWeight: 'bold',
    },
    '& td': {
      padding: '10px',
      textAlign: 'left',
      ...shorthands.border('1px', 'solid', 'var(--colorBrandForeground1)'),
      backgroundColor: 'var(--color-neutral-background2)', // Custom token
    },
    '& thead': {
      backgroundColor: 'var(--color-neutral-background3)',
    },
    '& tbody tr:nth-child(even)': {
      backgroundColor: '#F5F5F5', // Light gray for even rows
    },
    '& tbody tr:nth-child(odd)': {
      backgroundColor: '#FFFFFF', // White for odd rows
    },
  },
});



interface ChatHistoryItemProps {
  message: IChatMessage;
  messageIndex: number;
}

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({ message, messageIndex }) => {
  const classes = useClasses();
  const chat = useChat();

  const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
  const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);
  const [showCitationCards, setShowCitationCards] = useState(false);
  React.useEffect(() => {
    console.log('activeUserInfo', activeUserInfo);
  }, []);

  const isDefaultUser = message.userId === DefaultChatUser.id;
  // const isMe = isDefaultUser || (message.authorRole === AuthorRoles.User && message.userId === activeUserInfo?.id);
  const isMe = isDefaultUser || (message.authorRole === AuthorRoles.User);
  const isBot = message.authorRole === AuthorRoles.Bot;
  const user = isDefaultUser
    ? DefaultChatUser
    : chat.getChatUserById(message.userName, selectedId, conversations[selectedId].users);
  const fullName = user?.fullName ?? message.userName;

  const [messagedCopied, setMessageCopied] = useState(false);

  const copyOnClick = async () => {
    await navigator.clipboard.writeText(message.content).then(() => {
      setMessageCopied(true);
      setTimeout(() => {
        setMessageCopied(false);
      }, 2000);
    });
  };

  const avatar: AvatarProps = isBot
    ? { image: { src: conversations[selectedId].botProfilePicture } }
    : isDefaultUser
      ? { idForColor: selectedId, color: 'colorful', image: { src: Constants.connectUser.connectUsersIcon } }
      : { name: fullName, color: 'colorful' };

  let content: JSX.Element;
  if (isBot && message.type === ChatMessageType.Plan) {
    content = <PlanViewer message={message} messageIndex={messageIndex} />;
  } else if (message.type === ChatMessageType.Document) {
    content = <ChatHistoryDocumentContent isMe={isMe} message={message} />;
  }
  else {
    if (message.content.includes('adaptivecards.io')) { //show cards
      // const flightUpdate = {
      //   $schema: "http://adaptivecards.io/schemas/adaptive-card.json",
      //   type: "AdaptiveCard",
      //   version: "1.0",
      //   speak:
      //     "Flight KL0605 to San Fransisco has been delayed.It will not leave until 10:10 AM.",
      //   body: [
      //     {
      //       type: "ColumnSet",
      //       columns: [
      //         {
      //           type: "Column",
      //           width: "auto",
      //           items: [
      //             {
      //               type: "Image",
      //               size: "small",
      //               url: "http://adaptivecards.io/content/airplane.png"
      //             }
      //           ]
      //         },
      //         {
      //           type: "Column",
      //           width: "stretch",
      //           items: [
      //             {
      //               type: "TextBlock",
      //               text: "Flight Status",
      //               horizontalAlignment: "right",
      //               isSubtle: true
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "DELAYED",
      //               horizontalAlignment: "right",
      //               spacing: "none",
      //               size: "large",
      //               color: "attention"
      //             }
      //           ]
      //         }
      //       ]
      //     },
      //     {
      //       type: "ColumnSet",
      //       separator: true,
      //       spacing: "medium",
      //       columns: [
      //         {
      //           type: "Column",
      //           width: "stretch",
      //           items: [
      //             {
      //               type: "TextBlock",
      //               text: "Passengers",
      //               isSubtle: true,
      //               weight: "bolder"
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "Sarah Hum",
      //               spacing: "small"
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "Jeremy Goldberg",
      //               spacing: "small"
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "Evan Litvak",
      //               spacing: "small"
      //             }
      //           ]
      //         },
      //         {
      //           type: "Column",
      //           width: "auto",
      //           items: [
      //             {
      //               type: "TextBlock",
      //               text: "Seat",
      //               horizontalAlignment: "right",
      //               isSubtle: true,
      //               weight: "bolder"
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "14A",
      //               horizontalAlignment: "right",
      //               spacing: "small"
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "14B",
      //               horizontalAlignment: "right",
      //               spacing: "small"
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "14C",
      //               horizontalAlignment: "right",
      //               spacing: "small"
      //             }
      //           ]
      //         }
      //       ]
      //     },
      //     {
      //       type: "ColumnSet",
      //       spacing: "medium",
      //       separator: true,
      //       columns: [
      //         {
      //           type: "Column",
      //           width: 1,
      //           items: [
      //             {
      //               type: "TextBlock",
      //               text: "Flight",
      //               isSubtle: true,
      //               weight: "bolder"
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "KL0605",
      //               spacing: "small"
      //             }
      //           ]
      //         },
      //         {
      //           type: "Column",
      //           width: 1,
      //           items: [
      //             {
      //               type: "TextBlock",
      //               text: "Departs",
      //               isSubtle: true,
      //               horizontalAlignment: "center",
      //               weight: "bolder"
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "10:10 AM",
      //               color: "attention",
      //               weight: "bolder",
      //               horizontalAlignment: "center",
      //               spacing: "small"
      //             }
      //           ]
      //         },
      //         {
      //           type: "Column",
      //           width: 1,
      //           items: [
      //             {
      //               type: "TextBlock",
      //               text: "Arrives",
      //               isSubtle: true,
      //               horizontalAlignment: "right",
      //               weight: "bolder"
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "12:00 AM",
      //               color: "attention",
      //               horizontalAlignment: "right",
      //               weight: "bolder",
      //               spacing: "small"
      //             }
      //           ]
      //         }
      //       ]
      //     },
      //     {
      //       type: "ColumnSet",
      //       spacing: "medium",
      //       separator: true,
      //       columns: [
      //         {
      //           type: "Column",
      //           width: 1,
      //           items: [
      //             {
      //               type: "TextBlock",
      //               text: "Amsterdam",
      //               isSubtle: true
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "AMS",
      //               size: "extraLarge",
      //               color: "accent",
      //               spacing: "none"
      //             }
      //           ]
      //         },
      //         {
      //           type: "Column",
      //           width: "auto",
      //           items: [
      //             {
      //               type: "TextBlock",
      //               text: " "
      //             },
      //             {
      //               type: "Image",
      //               url: "http://adaptivecards.io/content/airplane.png",
      //               size: "small"
      //             }
      //           ]
      //         },
      //         {
      //           type: "Column",
      //           width: 1,
      //           items: [
      //             {
      //               type: "TextBlock",
      //               text: "San Francisco",
      //               isSubtle: true,
      //               horizontalAlignment: "right"
      //             },
      //             {
      //               type: "TextBlock",
      //               text: "SFO",
      //               horizontalAlignment: "right",
      //               size: "extraLarge",
      //               color: "accent",
      //               spacing: "none"
      //             }
      //           ]
      //         }
      //       ]
      //     }
      //   ]
      // };
      // content = <AdaptiveCard card={flightUpdate} />
      const cardContent: string = JSON.parse(message.content) as string;
      content = <AdaptiveCard card={cardContent} />
    }
    else if (message.type == ChatMessageType.Map) { //show map
      content = message.content.length === 0 ? <TypingIndicator /> :
        <APIProvider apiKey={process.env.GOOGLE_MAPS_API_KEY ?? ''}>
          <Map
            style={{ width: '30vw', height: '40vh' }}
            defaultCenter={{ lat: 23.7821311, lng: 90.3961418 }}
            defaultZoom={14}
            gestureHandling={'greedy'}
            disableDefaultUI={true}
          />
        </APIProvider>
    }
    else {
      content = isBot && message.content.length === 0 ? <TypingIndicator /> : <ChatHistoryTextContent message={message} />;
    }
  }


  // }

  // TODO: [Issue #42] Persistent RLHF, hook up to model
  // Currently for demonstration purposes only, no feedback is actually sent to kernel / model
  const showShowRLHFMessage =
    features[FeatureKeys.RLHF].enabled &&
    message.userFeedback === UserFeedback.Requested &&
    messageIndex === conversations[selectedId].messages.length - 1 &&
    message.userId === 'Bot';

  const messageCitations = message.citations ?? [];
  const showMessageCitation = messageCitations.length > 0;
  const showExtra = showMessageCitation || showShowRLHFMessage || showCitationCards;
  // const showExtra = showMessageCitation || showShowRLHFMessage;

  return (
    <div
      className={isMe ? mergeClasses(classes.root, classes.alignEnd) : classes.root}
      // The following data attributes are needed for CI and testing
      data-testid={`chat-history-item-${messageIndex}`}
      data-username={fullName}
      data-content={utils.formatChatTextContent(message.content)}
    >
      {
        <Persona
          className={classes.persona}
          avatar={avatar}
          presence={
            !features[FeatureKeys.SimplifiedExperience].enabled && !isMe
              ? { status: 'available' }
              : undefined
          }
        />
      }
      <div className={mergeClasses(classes.customTable, isMe ? mergeClasses(classes.item, classes.me) : classes.item)}>
        <div className={classes.header}>
          {/* {!isMe && <Text weight="semibold">{fullName}</Text>} */}
          <Text weight="semibold">{fullName}</Text>
          <Text className={classes.time}>{timestampToDateString(message.timestamp, true)}</Text>
          {isBot && <PromptDialog message={message} />}
          {isBot && message.prompt && (
            <Tooltip content={messagedCopied ? 'Copied' : 'Copy text'} relationship="label">
              <Button
                icon={messagedCopied ? <ClipboardTask20Regular /> : <Clipboard20Regular />}
                appearance="transparent"
                className={classes.orangeButton}
                onClick={() => {
                  void copyOnClick();
                }}
              />
            </Tooltip>
          )}
        </div>
        {content}
        {showExtra && (
          <div className={classes.controls}>
            {showMessageCitation && (
              <ToggleButton
                appearance="subtle"
                checked={showCitationCards}
                className={classes.citationButton}
                icon={showCitationCards ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                iconPosition="after"
                onClick={() => {
                  setShowCitationCards(!showCitationCards);
                }}
                size="small"
              >
                {`${messageCitations.length} ${messageCitations.length === 1 ? 'citation' : 'citations'
                  }`}
              </ToggleButton>
            )}
            {showShowRLHFMessage && (
              <div className={classes.rlhf}>{<UserFeedbackActions messageIndex={messageIndex} />}</div>
            )}
            {showCitationCards && <CitationCards message={message} />}
          </div>
        )}
      </div>
      {features[FeatureKeys.RLHF].enabled && message.userFeedback === UserFeedback.Positive && (
        <ThumbLikeFilled color="gray" />
      )}
      {features[FeatureKeys.RLHF].enabled && message.userFeedback === UserFeedback.Negative && (
        <ThumbDislikeFilled color="gray" />
      )}
    </div>
  );
};
